// src/pages/profile.tsx
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TransactionItem from "@/components/transaction-item";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  User as UserIcon,
  Coins,
  TrendingUp,
  Download,
  Copy,
  Users,
  Activity,
  Lightbulb,
  Share2,
  Gift,
  Percent,
  UserCircle,
} from "lucide-react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { AuditLog } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";

const fetchTransactionHistory = async (): Promise<AuditLog[]> => {
    const res = await fetch("/api/profile/transactions");
    if (!res.ok) {
      if (res.status === 401) return [];
      throw new Error("Failed to fetch transaction history");
    }
    return res.json();
  };

const toDate = (value: any): Date => {
  if (!value) return new Date(0);
  if (typeof value === "object" && value !== null && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000 + (value.nanoseconds ? Math.floor(value.nanoseconds / 1e6) : 0));
  }
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
  }
  if (typeof value === "number") {
    if (value < 1e12) return new Date(value * 1000);
    return new Date(value);
  }
  return new Date();
};

const Profile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [visibleTransactions, setVisibleTransactions] = useState(5);

  const { data, isLoading } = useQuery({
    queryKey: ["transactionHistory", user?.id],
    queryFn: fetchTransactionHistory,
    enabled: !!user?.id,
    staleTime: 1000 * 15, // 15 seconds
    refetchOnWindowFocus: false,
  });

  const transactionHistory = useMemo(() => {
    if (!data) return [];

    return data.map((log) => {
      let description = "Unknown Transaction";
      let amount = 0;
      let status: "completed" | "pending" | "failed" = "completed";

      switch (log.action) {
        case "offer_completion_credit":
          description = `Offer: ${log.details?.provider ?? "unknown"}`;
          amount = log.details?.tokens ?? 0;
          break;
        case "promo_redeem":
          description = `Promo: ${log.details?.code ?? "unknown"}`;
          amount = log.details?.reward ?? 0;
          break;
        case "withdrawal_success":
          description = "Robux Withdrawal";
          amount = -(log.details?.tokensDeducted ?? 0);
          status = "completed";
          break;
        case "withdrawal_refund":
          description = "Withdrawal Refund";
          amount = log.details?.refunded ?? 0;
          status = "completed";
          break;
        case "sponsorship_purchase":
          description = `Sponsored Ad: ${log.details?.packageName ?? "unknown"}`;
          amount = -(log.details?.cost ?? 0);
          break;
        case "sponsorship_delete_refund":
          description = `Ad Refund: ${log.details?.gameName ?? "unknown"}`;
          amount = log.details?.refundedAmount ?? 0;
          break;
        case "sponsored_ad_reward":
          description = `Sponsored Game: ${log.details?.gameName ?? "unknown"}`;
          amount = log.details?.tokens ?? 0;
          break;
        case "bot_token_credit":
          description = `Admin Credit: ${log.details?.reason ?? "admin adjustment"}`;
          amount = log.details?.amount ?? 0;
          break;
        case "clip_reward":
          description = `Clip Reward: ${log.details?.videoTitle ?? "YouTube Clip"}`;
          amount = log.details?.tokens ?? 0;
          break;
        case "stripe_purchase":
          description = "Tokens Purchase";
          amount = log.details?.tokens ?? 0;
          break;
      }

      return {
        id: log.id,
        type: log.action as any,
        description,
        amount,
        status,
        createdAt: toDate(log.timestamp),
        relatedId: log.targetId,
      };
    }).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [data]);

  const copyReferralLink = () => {
    if (!user) return;
    const referralLink = `http://bloxboost.com/login?ref=${(user as any).referralCode}`;
    navigator.clipboard.writeText(referralLink).catch(() => {
      toast({ title: "Copy failed", description: "Could not copy referral link", variant: "destructive" });
    });
    toast({
      title: "Copied!",
      description: "Referral link copied to clipboard",
    });
  };

  const memberSince = (() => {
    const createdAtFromUser = (user as any)?.createdAt ?? (user as any)?.created_at ?? null;
    if (createdAtFromUser) return toDate(createdAtFromUser);
    const metadataCreation = (user as any)?.metadata?.creationTime ?? (user as any)?.creationTime ?? null;
    if (metadataCreation) return toDate(metadataCreation);
    return null;
  })();

  return (
    <div className="py-20 px-4 bg-card/20">
      <div className="container mx-auto max-w-6xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center">
            <UserCircle className="w-8 h-8 mr-3 text-primary"/>
            Your Profile
          </h1>
          <p className="text-xl text-muted-foreground">Track your progress and manage your account</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="lg:col-span-1">
            <Card className="glass-effect border-border">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <motion.div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-secondary mx-auto mb-4 flex items-center justify-center" whileHover={{ scale: 1.1 }} data-testid="user-avatar-large">
                    {((user as any)?.avatarUrl) ? (
                      <img src={(user as any).avatarUrl} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <UserIcon className="w-12 h-12 text-white" />
                    )}
                  </motion.div>

                  <h3 className="text-xl font-semibold" data-testid="user-display-name">
                    {(user as any)?.displayName ?? (user as any)?.username ?? "Unknown User"}
                  </h3>
                  <p className="text-sm text-muted-foreground" data-testid="user-username">
                    @{(user as any)?.username ?? ((user as any)?.displayName ?? "unknown")}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30">
                    <div className="flex items-center space-x-2">
                      <Coins className="w-4 h-4 text-primary" />
                      <span className="text-sm">Current Balance</span>
                    </div>
                    <span className="font-semibold text-primary" data-testid="current-balance">
                      {(user as any)?.tokenBalance?.toLocaleString?.() ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="w-4 h-4 text-accent" />
                      <span className="text-sm">Total Earned</span>
                    </div>
                    <span className="font-semibold text-accent" data-testid="total-earned">
                      {(user as any)?.totalEarned?.toLocaleString?.() ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30">
                    <div className="flex items-center space-x-2">
                      <Download className="w-4 h-4 text-secondary" />
                      <span className="text-sm">Total Withdrawn</span>
                    </div>
                    <span className="font-semibold text-secondary" data-testid="total-withdrawn">
                      {(user as any)?.totalWithdrawn?.toLocaleString?.() ?? 0}
                    </span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-.border">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Member since</span>
                    <span data-testid="member-since">{memberSince ? memberSince.toLocaleDateString() : "Unknown"}</span>
                  </div>
                </div>

                {(user as any)?.role === "admin" && (
                  <div className="mt-6">
                    <Button onClick={() => setLocation("/admin")} className="w-full">Admin Panel</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <div className="lg:col-span-2 space-y-8">
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <Card className="glass-effect border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center space-x-2">
                      <Activity className="w-5 h-5" />
                      <span>Transaction History</span>
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    {isLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex items-center space-x-4 p-4 rounded-lg bg-background/30">
                          <Skeleton className="h-10 w-10 rounded-full bg-muted/50" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-3/4 bg-muted/50" />
                            <Skeleton className="h-4 w-1/2 bg-muted/50" />
                          </div>
                        </div>
                      ))
                    ) : transactionHistory.length > 0 ? (
                      transactionHistory.slice(0, visibleTransactions).map((transaction, index) => (
                        <motion.div key={transaction.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * index, duration: 0.4 }}>
                          <TransactionItem {...transaction} />
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No transactions yet</p>
                        <p className="text-sm">Start earning to see your transaction history!</p>
                      </div>
                    )}
                  </div>
                  {transactionHistory.length > visibleTransactions && (
                    <div className="text-center mt-4">
                      <Button onClick={() => setVisibleTransactions(prev => prev + 10)}>
                        Load More
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
              <Card className="glass-effect border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center space-x-2">
                        <Users className="w-5 h-5" />
                        <span>Referral Program</span>
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">Earn 10% of your referrals' earnings</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2d font-bold text-secondary" data-testid="referral-count">
                        {(user as any)?.referralEarnings ?? 0}
                      </div>
                      <div className="text-xs text-muted-foreground">tokens earned</div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Your Referral Link</label>
                    <div className="flex space-x-2">
                      <Input readOnly value={`http://bloxboost.com/login?ref=${(user as any)?.referralCode ?? "unknown"}`} className="flex-1 font-mono text-sm bg-input" data-testid="referral-link" />
                      <Button onClick={copyReferralLink} data-testid="button-copy-referral">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-background/30 border border-border text-center">
                      <div className="text-lg font-bold text-secondary" data-testid="referral-earnings">
                        {(user as any)?.referralEarnings?.toLocaleString?.() ?? 0}
                      </div>
                      <div className="text-xs text-muted-foreground">tokens earned</div>
                    </div>
                    <div className="p-4 rounded-lg bg-background/30 border border-border text-center">
                      <div className="text-lg font-bold text-accent" data-testid="active-referrals">
                        0
                      </div>
                      <div className="text-xs text-muted-foreground">active this month</div>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <h4 className="font-medium text-primary mb-2 flex items-center">
                      <Lightbulb className="w-4 h-4 mr-2" />
                      Referral Tips
                    </h4>
                    <div className="text-sm text-muted-foreground space-y-2">
                      <div className="flex items-start">
                        <Share2 className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                        <span>Share your link on social media</span>
                      </div>
                      <div className="flex items-start">
                        <Gift className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                        <span>Invite friends who play Roblox</span>
                      </div>
                      <div className="flex items-start">
                        <Percent className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                        <span>Earn 10% of everything they earn</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
